import React, { useState, useEffect } from 'react';
import {
  Button, Form, Container, Row, Col, Modal, Dropdown
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useImageApi from '../../managers/ImageManager';
import useProductApi from '../../managers/ProductManager';
import Loading, { LoadingStates } from '../../components/Loading';
import { useAuth0 } from '@auth0/auth0-react';
import { FaPalette, FaVectorSquare, FaTree } from 'react-icons/fa6';
import { Helmet } from 'react-helmet-async';

import useCatalogApi, { CatalogItem } from '../../managers/CatalogManager';
import { Spinner } from 'react-bootstrap';
import CatalogItemSelector from './component/CatalogItemSelector';
import FileUpload from './component/FileUpload';
import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';

// Creative loading messages for AI generation
const GENERATION_MESSAGES = [
  "Bringing your vision to life...",
  "AI is painting your masterpiece...",
  "Take a deep breath, magic is happening...",
  "Close your eyes and dream while we create...",
  "Transforming pixels into perfection...",
  "Your artwork is emerging from digital dreams...",
  "Weaving creativity with AI...",
  "Crafting something beautiful just for you...",
  "Mixing imagination with innovation...",
  "Watch your ideas bloom into reality..."
];

const Create: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    prompt?: string;
    catalogItemId?: string;
    catalogItemVariantId?: string;
  } | null;

  const [loadingState, setLoadingState] = useState(LoadingStates.SUCCESS);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [loadingCatalogState, setLoadingCatalogState] = useState(LoadingStates.SUCCESS);
  const [loadingCatalogMessage, setLoadingCatalogMessage] = useState("Loading Products...");
  const [productImgUrl, setProductImgUrl] = useState('/logo512.png');
  const [generatedProduct, setGeneratedProduct] = useState<any>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [vendorName, setVendorName] = useState('openai');

  const [imageModel, setImageModel] = useState('gpt-image-2');
  const [imageModelDropdownOpen, setImageModelDropdownOpen] = useState(false);
  const toggleImageModelDropdown = () => setImageModelDropdownOpen((prev) => !prev);

  const [imageStyle, setImageStyle] = useState('vivid');
  const [styleDropdownOpen, setStyleDropdownOpen] = useState(false);
  const toggleStyleDropdown = () => setStyleDropdownOpen((prev) => !prev);

  const [prompt, setPrompt] = useState(state?.prompt || '');
  const [imageShape, setImageShape] = useState('square');

  const [catalogItemId, setCatalogItemId] = useState(state?.catalogItemId || '');
  const [catalogItemVariantId, setCatalogItemVariantId] = useState(state?.catalogItemVariantId || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);

  const { getCatalogItems } = useCatalogApi();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);

  const imageModelIcons: Record<string, JSX.Element> = {
    "gpt-image-2": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-2
      </span>
    ),
    "gpt-image-1": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-1
      </span>
    ),
    "gpt-image-1.5": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-1.5
      </span>
    ),
    "dall-e-3": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        dall-e-3
      </span>
    ),
  };

  const styleIcons: Record<string, JSX.Element> = {
    vivid: (
      <span className="d-flex align-items-center gap-2">
        <FaPalette />
        Vivid
      </span>
    ),
    natural: (
      <span className="d-flex align-items-center gap-2">
        <FaTree />
        Natural
      </span>
    ),
  };

  useEffect(() => {
    setLoadingCatalogState(LoadingStates.LOADING);
    setLoadingCatalogMessage("Loading Products...");

    getCatalogItems()
      .then((res) => {
        const items = Array.isArray(res) ? res : res.items;

        if (Array.isArray(items)) {
          setCatalogItems(items);

          if (!catalogItemId && items.length > 0) {
            setCatalogItemId(items[0].id);
            if (items[0].variants?.length > 0) {
              setCatalogItemVariantId(items[0].variants[0].id);
            }
          }

          setLoadingCatalogState(LoadingStates.SUCCESS);
        } else {
          setLoadingCatalogState(LoadingStates.ERROR);
          setLoadingCatalogMessage("Failed to load products.");
          console.warn("Catalog API returned unexpected format", res);
        }
      })
      .catch((err) => {
        setLoadingCatalogState(LoadingStates.ERROR);
        setLoadingCatalogMessage("Failed to load products.");
        console.error("Failed to fetch catalog items:", err);
      });
  }, [getCatalogItems]);

  const selectedCatalogItem = catalogItems?.find(item => item.id === catalogItemId);
  const catalogVariants = selectedCatalogItem?.variants || [];
  const selectedCatalogVariant = catalogVariants?.find(variant => variant.id === catalogItemVariantId);

  const { generateProduct } = useProductApi();
  const { generateImage, generateImageAsync, uploadImage, getImageBaseUrl } = useImageApi();
  const { user } = useAuth0();

  const toggleModal = () => setModalOpen(!isModalOpen);

  // Cycle through generation messages every 15 seconds while loading
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (loadingState === LoadingStates.LOADING && !loadingMessage.includes("Product Preview")) {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % GENERATION_MESSAGES.length;
          setLoadingMessage(GENERATION_MESSAGES[newIndex]);
          return newIndex;
        });
      }, 15000); // 15 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loadingState, loadingMessage]);

  const handleGenerate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const usePrompt = !!prompt;
    const useFile = !!selectedFile;

    // Validate required fields
    if (!catalogItemId) {
      alert("Please select a product type.");
      return;
    }

    if (!catalogItemVariantId) {
      alert("Please select a product size/variant.");
      return;
    }

    if (!usePrompt && !useFile) {
      alert("Please enter a prompt or upload an image.");
      return;
    }

    const isUploadOnly = useFile && !usePrompt;
    const isPromptOnly = usePrompt && !useFile;
    const isPromptAndImage = useFile && usePrompt;

    // Start with a random creative message for AI generation
    const randomIndex = Math.floor(Math.random() * GENERATION_MESSAGES.length);
    setMessageIndex(randomIndex);
    setLoadingMessage(GENERATION_MESSAGES[randomIndex]);
    setLoadingState(LoadingStates.LOADING);

    try {
      let base64Image: string | undefined;

      if (useFile && selectedFile) {
        let fileToProcess = selectedFile;

        // Convert HEIC/HEIF files to JPEG
        if (selectedFile.type === 'image/heic' || selectedFile.type === 'image/heif' ||
          selectedFile.name.toLowerCase().endsWith('.heic') || selectedFile.name.toLowerCase().endsWith('.heif')) {
          try {
            console.log('Converting HEIC/HEIF file to JPEG...');
            setLoadingMessage('Converting image format...');

            const convertedBlob = await heic2any({
              blob: selectedFile,
              toType: 'image/jpeg',
              quality: 0.9
            });

            // heic2any can return Blob or Blob[], handle both cases
            const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            fileToProcess = new File([blob], selectedFile.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), {
              type: 'image/jpeg'
            });

            console.log('HEIC/HEIF conversion successful');
          } catch (conversionError) {
            console.error('Failed to convert HEIC/HEIF file:', conversionError);
            throw new Error('Failed to convert HEIC/HEIF image. Please try converting it to JPEG first.');
          }
        }

        // Compress image to ensure it's under payload limits
        // Base64 encoding adds ~33% overhead, so we target 5MB file size to stay under 7MB base64
        const fileSizeMB = fileToProcess.size / 1024 / 1024;
        console.log(`Original file size: ${fileSizeMB.toFixed(2)} MB`);

        let fileToRead = fileToProcess;
        if (fileSizeMB > 3) { // Compress if larger than 3MB
          try {
            console.log('Compressing image to reduce size...');
            setLoadingMessage('Optimizing image size...');

            const options = {
              maxSizeMB: 5, // Target 5MB max (will be ~6.6MB in base64)
              maxWidthOrHeight: 2048, // Max dimension for AI processing
              useWebWorker: true,
              fileType: 'image/jpeg',
              initialQuality: 0.85
            };

            const compressedFile = await imageCompression(fileToProcess, options);
            const compressedSizeMB = compressedFile.size / 1024 / 1024;
            console.log(`Compressed file size: ${compressedSizeMB.toFixed(2)} MB (${((1 - compressedSizeMB / fileSizeMB) * 100).toFixed(1)}% reduction)`);

            fileToRead = compressedFile;
          } catch (compressionError) {
            console.error('Image compression failed, using original:', compressionError);
            // Continue with uncompressed file - may still work if not too large
            fileToRead = fileToProcess;
          }
        }

        base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64Data = result.split(',')[1];
            const base64SizeMB = (base64Data.length * 0.75) / 1024 / 1024; // Approximate decoded size
            console.log(`Base64 encoded size: ~${base64SizeMB.toFixed(2)} MB`);

            if (base64SizeMB > 8) {
              reject(new Error('Image is too large even after compression. Please use a smaller image or reduce the resolution.'));
            } else {
              resolve(base64Data);
            }
          };
          reader.onerror = () => reject(new Error('Failed to read file.'));
          reader.readAsDataURL(fileToRead);
        });
      }

      let image;

      if (isUploadOnly) {
        // For upload-only, use the original sync method
        image = await uploadImage({
          userId: user?.sub || "0",
          action: 'upload',
          base64Image,
          imageName: 'Uploaded Image',
          imageTags: []
        });
      } else {
        // For generate or generate-from-upload, use the async method
        // Enhancement now happens automatically in the backend
        image = await generateImageAsync({
          userId: user?.sub || "0",
          action: isPromptOnly ? 'generate' : 'generate-from-upload',
          prompt,
          vendorId: vendorName || 'openai',
          imageModel: imageModel || 'gpt-image-2',
          imageShape: selectedCatalogVariant?.orientation || selectedCatalogItem?.orientation || imageShape,
          imageStyle,
          base64Image,
          catalogItemId,
          catalogItemVariantId
        });
      }

      console.log('Image result:', image);

      if (!image?.imageUrl) {
        // If status is completed but imageUrl is missing, try to construct it
        if (image?.status === 'completed' && image?.imageKey) {
          // Clean imageKey to prevent double slashes in URL
          const cleanImageKey = image.imageKey.startsWith('/')
            ? image.imageKey.substring(1)
            : image.imageKey;
          image.imageUrl = `${getImageBaseUrl()}/${cleanImageKey}`;
          console.log('Constructed imageUrl from imageKey:', image.imageUrl);
        } else {
          console.error('Image generation result missing imageUrl:', image);
          throw new Error(`Image generation failed. Status: ${image?.status || 'unknown'}, Error: ${image?.errorMessage || 'No error message'}`);
        }
      }

      console.log('Image generated successfully:', {
        imageId: image.imageId,
        imageUrl: image.imageUrl,
        imageName: image.imageName,
        requestedOrientation: selectedCatalogVariant?.orientation || selectedCatalogItem?.orientation || imageShape,
        catalogItemId,
        catalogItemVariantId,
        variantName: selectedCatalogVariant?.name
      });

      setLoadingMessage("Generating Product Preview...");

      // Small delay to help with eventual consistency issues
      await new Promise(resolve => setTimeout(resolve, 500));

      const product = await generateProduct({
        userId: user?.sub || "0",
        sourceImageUrl: image.imageUrl,
        sourceImageId: image.imageId,
        catalogItemId,
        catalogItemVariantId
      });

      console.log('Product generated successfully:', {
        productId: product.productId,
        previewImageUrls: product.previewImageUrls
      });

      if (!product?.previewImageUrls?.[0]) throw new Error('Product preview failed.');

      setLoadingState(LoadingStates.SUCCESS);
      // clear the form
      setPrompt('');
      setSelectedFile(null);

      // Navigate directly to product detail instead of showing modal
      const productUrl = `/products/${catalogItemId}/${selectedCatalogItem?.category || 'uncategorized'}/${product.productId}`;
      navigate(productUrl);
    } catch (err: any) {
      console.error(err);
      setLoadingMessage(err.message);
      setLoadingState(LoadingStates.ERROR);
    }
  };


  // Get preview image from selected catalog variant or item
  const getPreviewImage = (): string | undefined => {
    if (selectedCatalogVariant?.previewImageUrls?.[0]) {
      return selectedCatalogVariant.previewImageUrls[0];
    }
    if (selectedCatalogItem?.previewImageUrls?.[0]) {
      return selectedCatalogItem.previewImageUrls[0];
    }
    return undefined;
  };

  return (
    <>
      <Helmet>
        <title>Create Your Own | Wallscapes.AI</title>
        <meta name="description" content="Design your own custom AI-generated art for print-on-demand products. Create unique wall tapestries, posters, and more." />
      </Helmet>
      {/* Overlay Loading Screen */}
      {loadingState === LoadingStates.LOADING && (
        <Loading
          state={loadingState}
          message={loadingMessage}
          subtitle="Please don't close this window. We're creating something amazing for you!"
          overlay={true}
          image={getPreviewImage()}
        />
      )}

      <Container className="py-5">
        <Row className="align-items-center text-center justify-content-center mb-5">
          <Col md="10" lg="8">
            <h1 className="display-4">Design Your Own</h1>
            <p className="lead">
              Choose a product, describe your vision, and watch AI turn your words into art!
            </p>

            {/* Step 1: Choose a Product */}
            <Row className="mb-5 my-5">
              <Col xs="12">
                <h3 className="mb-3">Choose Your Product</h3>
              </Col>

              {loadingCatalogState !== LoadingStates.SUCCESS ? (
                <Col xs="12">
                  <Loading state={loadingCatalogState} message={loadingCatalogMessage} />
                </Col>
              ) : catalogItems.length === 0 ? (
                <Col xs="12">
                  <p className="lead">No products available.</p>
                </Col>
              ) : (
                <CatalogItemSelector
                  catalogItems={catalogItems}
                  selectedCatalogItemId={catalogItemId}
                  selectedVariantId={catalogItemVariantId}
                  onSelectItem={setCatalogItemId}
                  onSelectVariant={setCatalogItemVariantId}
                />
              )}
            </Row>


            {/* Step 2: Describe or Upload */}
            <Row className="mb-5">
              <Col>
                <h3 className="mb-3">Provide Text, Image, or Both</h3>
                <div className="bg-light border border-secondary rounded text-start p-3 mb-3">
                  <span className="badge bg-secondary me-2">Tip</span>
                  You can create in three ways:
                  <ul className="mt-2 mb-0">
                    <li><strong>Prompt only</strong> - type a prompt and don't upload an image</li>
                    <li><strong>Image only</strong> - upload an image and leave the prompt box blank to use it as-is</li>
                    <li><strong>Prompt + Image</strong> - upload an image and add a prompt to modify it</li>
                  </ul>
                </div>

                <Form onSubmit={handleGenerate}>
                  <Row className="align-items-stretch">
                    <Col className="mb-3">
                      <Form.Group className="h-100 d-flex flex-column">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          className="flex-grow-1"
                          placeholder="Type here to describe what you want to see!"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs="12" md="4" lg="3" className="text-center">
                      <FileUpload
                        onFileSelected={setSelectedFile}
                        accept="image/*"
                        className="bg-white"
                        placeholder={selectedFile ? selectedFile.name : 'Optionally provide an image.'}
                        showPreview
                      />
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 my-3">
                    {/* Image Model dropdown */}
                    <Dropdown show={imageModelDropdownOpen} onToggle={toggleImageModelDropdown}>
                      <Dropdown.Toggle className="btn btn-light">{imageModelIcons[imageModel]}</Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.entries(imageModelIcons).map(([model, icon]) => (
                          <Dropdown.Item key={model} active={imageModel === model} onClick={() => setImageModel(model)}>
                            {icon}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Image Style dropdown */}
                    <Dropdown show={styleDropdownOpen} onToggle={toggleStyleDropdown}>
                      <Dropdown.Toggle className="btn btn-light">{styleIcons[imageStyle]}</Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.entries(styleIcons).map(([style, icon]) => (
                          <Dropdown.Item key={style} active={imageStyle === style} onClick={() => setImageStyle(style)}>
                            {icon}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                  </div>

                  <Button
                    size="lg"
                    className="w-100"
                    variant="primary"
                    disabled={loadingState === LoadingStates.LOADING}
                    type="submit"
                  >
                    {selectedFile && prompt
                      ? 'Generate from Prompt + Image'
                      : selectedFile
                        ? 'Upload & Generate'
                        : 'Generate'}
                  </Button>
                </Form>
              </Col>
            </Row>
            {(loadingState === LoadingStates.ERROR) && (
              <Loading state={loadingState} message={loadingMessage} />
            )}
          </Col>
        </Row>

        <Modal
          show={isModalOpen}
          onHide={toggleModal}
          fullscreen={true}
          className="preview-modal"
        >
          <Modal.Body className="p-0 d-flex flex-column" style={{ height: '100vh', backgroundColor: '#000' }}>
            {/* Header with controls */}
            <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
              <h5 className="text-white mb-0">Product Preview</h5>
              <Button variant="outline-light" size="sm" onClick={toggleModal}>
                ✕ Close
              </Button>
            </div>

            {/* Image container - mobile first, centered */}
            <div className="flex-grow-1 d-flex align-items-center justify-content-center p-3">
              <img
                src={productImgUrl}
                alt="Product Preview"
                className="img-fluid"
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(255,255,255,0.1)'
                }}
              />
            </div>

            {/* Bottom action bar - mobile friendly */}
            <div className="d-flex flex-column flex-sm-row gap-2 p-3" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
              {generatedProduct && (
                <Button
                  variant="primary"
                  className="flex-grow-1"
                  onClick={() => {
                    const productUrl = `/products/${catalogItemId}/${selectedCatalogItem?.category || 'uncategorized'}/${generatedProduct.productId}`;
                    navigate(productUrl);
                  }}
                >
                  View Product Details
                </Button>
              )}
              <Button
                variant="success"
                className="flex-grow-1"
                onClick={() => {
                  toggleModal();
                  // Scroll to create another section
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Create Another
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
};

export default Create;